import { gql } from '@apollo/client'
import { useLazyQuery, useMutation } from '@apollo/client/react'
import { message } from '@homemaster/jslib'
import PersonIcon from '@mui/icons-material/Person'
import {
  Avatar,
  Box,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
} from '@mui/material'
import * as ww from '@wecom/jssdk'
import isEmpty from 'lodash/isEmpty'
import isEqual from 'lodash/isEqual'
import { useCallback, useEffect, useState } from 'react'
import {
  type BasicUserFieldsFragment,
  GetWxworkChatUserDocument,
  UpdateUserPermissionsDocument,
} from '../gql/graphql'

const PERMISSIONS = new Map([['event.create', '发起比赛']])
gql`
  query GetWxworkChatUser($wxworkUid: String, $chatId: String) {
    wxworkChatUser(wxworkUid: $wxworkUid, chatId: $chatId) {
      ...BasicUserFields
    }
  }

  mutation UpdateUserPermissions($userId: ID!, $permissions: [String!]) {
    updateUserPermissions(userId: $userId, permissions: $permissions) {
      ...BasicUserFields
    }
  }
`

/**
 *
 * The view used then the app is entered from the wxwork chat tool
 */
export default function ChatView() {
  const [user, setUser] = useState<BasicUserFieldsFragment | null>()
  const [getChatUser] = useLazyQuery(GetWxworkChatUserDocument)
  const [updateUserPermissions] = useMutation(UpdateUserPermissionsDocument)
  const [permissions, setPermissions] = useState<string[]>([])

  useEffect(() => {
    ;(async () => {
      let variables = {}

      const { entry } = await ww.getContext()
      if (entry === 'single_chat_tools' || entry === 'single_kf_tools') {
        const ret = await ww.getCurExternalContact()
        variables = { wxworkUid: ret.userId }
      } else if (entry === 'group_chat_tools') {
        const ret = await ww.getCurExternalChat()
        variables = { chatId: ret.chatId }
      } else {
        throw new Error(`Unsupported entry: ${entry}`)
      }

      const { data } = await getChatUser({ variables })
      setUser(data ? (data.wxworkChatUser as BasicUserFieldsFragment) : undefined)
    })()
  }, [getChatUser])

  useEffect(() => {
    setPermissions(user?.permissions || [])
  }, [user])

  const handleTogglePermission = useCallback(
    (p: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setPermissions((prev) =>
        event.target.checked ? [...prev, p] : prev.filter((x) => x !== p),
      )
    },
    [],
  )

  const handleSubmit = useCallback(async () => {
    const user_id = user?.id
    if (!user_id) return

    const new_permissions = new Set(permissions)
    const old_permissions = new Set(user.permissions || [])
    if (isEqual(new_permissions, old_permissions)) {
      message.info('权限未更改，无需提交')
    } else {
      try {
        const { data } = await updateUserPermissions({
          variables: {
            userId: user_id,
            permissions: isEmpty(permissions) ? null : permissions,
          },
        })
        if (data?.updateUserPermissions) {
          setUser(data.updateUserPermissions as BasicUserFieldsFragment)
          message.success('权限更新成功')
        }
      } catch (e) {
        console.error(e)
      }
    }
  }, [permissions, user, updateUserPermissions])

  if (user === undefined) return
  else if (user === null) {
    return <div>无法获取用户信息</div>
  }

  return (
    <Box sx={{ height: '100vh', display: 'flex', flexDirection: 'column', p: 1 }}>
      <Box>
        <Avatar
          sx={(theme) => ({
            mt: 2,
            marginLeft: 'auto',
            marginRight: 'auto',
            color: theme.palette.primary.main,
            width: theme.spacing(5),
            height: theme.spacing(5),
            cursor: 'pointer',
          })}
          src={user.avatarUrl || undefined}
        >
          {!user.avatarUrl && <PersonIcon />}
        </Avatar>
        <Typography align="center" mt={1}>
          {user.name}
        </Typography>
      </Box>

      <Box>
        <Typography mt={1}>用户权限</Typography>
      </Box>

      <FormGroup sx={{ overflowY: 'auto', flexGrow: 1 }}>
        {Array.from(PERMISSIONS.entries()).map(([p, l]) => (
          <FormControlLabel
            key={p}
            control={
              <Checkbox
                checked={permissions.includes(p)}
                onChange={handleTogglePermission(p)}
              />
            }
            label={l}
          />
        ))}
      </FormGroup>

      <Box sx={{ textAlign: 'center' }}>
        <Button variant="contained" onClick={handleSubmit}>
          提交
        </Button>
      </Box>
    </Box>
  )
}
